package com.havn.app.core.designsystem

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.havnContactAvatarPreviewSource
import com.havn.app.core.platform.havnShouldAvoidFloatingNumericKeyboard
import com.havn.app.ui.theme.LocalHavnThemeTokens
import com.havn.app.ui.theme.HavnThemeTokens
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween

enum class HavnButtonStyle {
    Primary,
    Secondary,
    Ghost,
    Danger
}

private fun havnActionAccent(tokens: HavnThemeTokens): Color =
    if (tokens.isLight) Color(0xFF9A5F00) else tokens.primaryAccent

private fun havnActionAccentSecondary(tokens: HavnThemeTokens): Color =
    if (tokens.isLight) Color(0xFF8A4F00) else tokens.secondaryAccent

@Composable
fun HavnButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    style: HavnButtonStyle = HavnButtonStyle.Primary,
    enabled: Boolean = true,
    loading: Boolean = false,
    showLoadingText: Boolean = false,
    leadingIcon: HavnIconKey? = null,
    expandOnWeb: Boolean = true
) {
    val tokens = LocalHavnThemeTokens.current
    val actionAccent = havnActionAccent(tokens)
    val actionAccentSecondary = havnActionAccentSecondary(tokens)
    val buttonModifier = if (havnIsWebShell && !expandOnWeb) {
        modifier
            .widthIn(max = 360.dp)
            .heightIn(min = 48.dp)
    } else {
        modifier.heightIn(min = 52.dp)
    }

    if (havnUsesLiquidButtons) {
        HavnLiquidButton(
            text = text,
            onClick = onClick,
            modifier = buttonModifier,
            style = style,
            enabled = enabled,
            loading = loading,
            showLoadingText = showLoadingText,
            leadingIcon = leadingIcon
        )
        return
    }

    val content: @Composable (Color, Boolean) -> Unit = { contentColor, _ ->
        if (loading && !showLoadingText) {
            CircularProgressIndicator(
                modifier = Modifier.size(20.dp),
                strokeWidth = 2.dp,
                color = when (style) {
                    HavnButtonStyle.Primary -> tokens.primaryAccentContent
                    HavnButtonStyle.Secondary,
                    HavnButtonStyle.Ghost -> actionAccent
                    HavnButtonStyle.Danger -> tokens.danger
                }
            )
        } else {
            Row(
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (loading && showLoadingText) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(18.dp),
                        strokeWidth = 2.dp,
                        color = contentColor
                    )
                    Spacer(Modifier.width(HavnSpacing.sm))
                } else if (leadingIcon != null) {
                    HavnIcon(
                        icon = leadingIcon,
                        contentDescription = null,
                        modifier = Modifier.size(18.dp),
                        tint = contentColor
                    )
                    Spacer(Modifier.width(HavnSpacing.sm))
                }
                Text(
                    text,
                    color = contentColor,
                    style = MaterialTheme.typography.labelLarge
                )
            }
        }
    }

    when (style) {
        HavnButtonStyle.Primary -> HavnAndroidGoldButton(
            onClick = onClick,
            enabled = enabled,
            loading = loading,
            modifier = buttonModifier
        ) {
            content(tokens.primaryAccentContent, false)
        }
        HavnButtonStyle.Secondary -> OutlinedButton(
            onClick = onClick,
            enabled = enabled && !loading,
            modifier = buttonModifier,
            shape = MaterialTheme.shapes.medium,
            border = BorderStroke(1.dp, if (enabled) actionAccent else tokens.border),
            colors = ButtonDefaults.outlinedButtonColors(
                contentColor = actionAccent,
                disabledContentColor = tokens.mutedText
            ),
            contentPadding = PaddingValues(horizontal = HavnSpacing.lg, vertical = HavnSpacing.md)
        ) {
            content(actionAccent, true)
        }
        HavnButtonStyle.Ghost -> TextButton(
            onClick = onClick,
            enabled = enabled && !loading,
            modifier = buttonModifier,
            shape = MaterialTheme.shapes.medium,
            colors = ButtonDefaults.textButtonColors(
                contentColor = actionAccentSecondary,
                disabledContentColor = tokens.mutedText
            ),
            contentPadding = PaddingValues(horizontal = HavnSpacing.lg, vertical = HavnSpacing.md)
        ) {
            content(actionAccentSecondary, true)
        }
        HavnButtonStyle.Danger -> TextButton(
            onClick = onClick,
            enabled = enabled && !loading,
            modifier = buttonModifier,
            shape = MaterialTheme.shapes.medium,
            colors = ButtonDefaults.textButtonColors(
                contentColor = tokens.danger,
                disabledContentColor = tokens.mutedText
            ),
            contentPadding = PaddingValues(horizontal = HavnSpacing.lg, vertical = HavnSpacing.md)
        ) {
            content(tokens.danger, true)
        }
    }
}

@Composable
private fun HavnAndroidGoldButton(
    onClick: () -> Unit,
    enabled: Boolean,
    loading: Boolean,
    modifier: Modifier,
    content: @Composable () -> Unit
) {
    val tokens = LocalHavnThemeTokens.current
    val shape = RoundedCornerShape(8.dp)
    Box(
        modifier = modifier
            .alpha(if (enabled) 1f else 0.46f)
            .clip(shape)
            .background(tokens.primaryAccent)
            .border(BorderStroke(1.dp, tokens.secondaryAccent.copy(alpha = 0.78f)), shape)
            .then(if (enabled && !loading) Modifier.clickable(onClick = onClick) else Modifier)
            .padding(horizontal = HavnSpacing.lg, vertical = 13.dp),
        contentAlignment = Alignment.Center
    ) {
        content()
    }
}

@Composable
private fun HavnLiquidButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier,
    style: HavnButtonStyle,
    enabled: Boolean,
    loading: Boolean,
    showLoadingText: Boolean,
    leadingIcon: HavnIconKey?
) {
    val tokens = LocalHavnThemeTokens.current
    val actionAccent = havnActionAccent(tokens)
    val actionAccentSecondary = havnActionAccentSecondary(tokens)
    val active = enabled && !loading
    val shape = RoundedCornerShape(26.dp)
    val borderColor = when (style) {
        HavnButtonStyle.Primary -> actionAccent.copy(alpha = if (tokens.isLight) 0.58f else 0.38f)
        HavnButtonStyle.Secondary -> tokens.primaryText.copy(alpha = 0.18f)
        HavnButtonStyle.Ghost -> tokens.primaryText.copy(alpha = 0.12f)
        HavnButtonStyle.Danger -> tokens.danger.copy(alpha = 0.34f)
    }
    val contentColor = when (style) {
        HavnButtonStyle.Primary -> actionAccentSecondary
        HavnButtonStyle.Secondary -> tokens.primaryText
        HavnButtonStyle.Ghost -> tokens.primaryText
        HavnButtonStyle.Danger -> tokens.danger
    }
    val fill = when (style) {
        HavnButtonStyle.Primary -> if (tokens.isLight) {
            Brush.verticalGradient(
                listOf(
                    actionAccent.copy(alpha = 0.13f),
                    tokens.primaryAccent.copy(alpha = 0.22f),
                    tokens.card.copy(alpha = 0.62f)
                )
            )
        } else {
            Brush.verticalGradient(
                listOf(
                    tokens.primaryText.copy(alpha = 0.11f),
                    tokens.secondaryAccent.copy(alpha = 0.18f),
                    tokens.card.copy(alpha = 0.46f)
                )
            )
        }
        HavnButtonStyle.Secondary -> Brush.verticalGradient(
            listOf(
                tokens.primaryText.copy(alpha = 0.08f),
                tokens.card.copy(alpha = 0.42f),
                tokens.surface.copy(alpha = 0.3f)
            )
        )
        HavnButtonStyle.Ghost -> Brush.verticalGradient(
            listOf(
                tokens.primaryText.copy(alpha = 0.04f),
                tokens.surface.copy(alpha = 0.16f)
            )
        )
        HavnButtonStyle.Danger -> Brush.verticalGradient(
            listOf(
                tokens.danger.copy(alpha = 0.1f),
                tokens.card.copy(alpha = 0.36f),
                tokens.surface.copy(alpha = 0.22f)
            )
        )
    }

    Box(
        modifier = modifier
            .alpha(if (enabled) 1f else 0.46f)
            .clip(shape)
            .background(fill)
            .border(BorderStroke(1.dp, borderColor), shape)
            .then(if (active) Modifier.clickable(onClick = onClick) else Modifier),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(1.dp)
                .align(Alignment.TopCenter)
                .background(tokens.primaryText.copy(alpha = 0.2f))
        )
        Box(
            modifier = Modifier.padding(horizontal = HavnSpacing.lg, vertical = HavnSpacing.md),
            contentAlignment = Alignment.Center
        ) {
            if (loading && !showLoadingText) {
                CircularProgressIndicator(
                    modifier = Modifier.size(20.dp),
                    strokeWidth = 2.dp,
                    color = contentColor
                )
            } else {
                Row(
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (loading && showLoadingText) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(18.dp),
                            strokeWidth = 2.dp,
                            color = contentColor
                        )
                        Spacer(Modifier.width(HavnSpacing.sm))
                    } else if (leadingIcon != null) {
                        HavnIcon(
                            icon = leadingIcon,
                            contentDescription = null,
                            modifier = Modifier.size(18.dp),
                            tint = contentColor
                        )
                        Spacer(Modifier.width(HavnSpacing.sm))
                    }
                    Text(
                        text,
                        color = contentColor,
                        style = MaterialTheme.typography.labelLarge
                    )
                }
            }
        }
    }
}

@Composable
fun HavnCard(
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    imageHeader: (@Composable BoxScope.() -> Unit)? = null,
    border: BorderStroke? = BorderStroke(1.dp, HavnCardBorder),
    content: @Composable () -> Unit
) {
    val tokens = LocalHavnThemeTokens.current
    val effectiveBorder = if (border == null) null else BorderStroke(1.dp, tokens.border)
    Surface(
        modifier = modifier
            .fillMaxWidth()
            .then(if (onClick != null) Modifier.clickable(onClick = onClick) else Modifier),
        shape = MaterialTheme.shapes.large,
        color = tokens.card,
        contentColor = tokens.primaryText,
        border = effectiveBorder,
        tonalElevation = 2.dp
    ) {
        Column {
            if (imageHeader != null) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .aspectRatio(1.62f),
                    content = imageHeader
                )
            }
            Box(Modifier.padding(HavnSpacing.md)) {
                content()
            }
        }
    }
}

@Composable
fun HavnPropertyCard(
    address: String,
    city: String,
    status: HavnStatus,
    visitorCount: Int,
    hotLeadCount: Int,
    nextOpenHouseTime: String,
    modifier: Modifier = Modifier,
    price: String? = null,
    image: (@Composable BoxScope.() -> Unit)? = null,
    onClick: (() -> Unit)? = null
) {
    val tokens = LocalHavnThemeTokens.current
    HavnCard(
        modifier = modifier,
        onClick = onClick,
        imageHeader = image ?: {
            DefaultPropertyImage()
            HavnStatusBadge(
                status = status,
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .padding(HavnSpacing.md)
            )
        }
    ) {
        Column {
            Text(
                text = address,
                color = tokens.primaryText,
                style = MaterialTheme.typography.titleLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Spacer(Modifier.height(HavnSpacing.xs))
            Text(city, color = tokens.secondaryText, style = MaterialTheme.typography.bodyLarge)
            price?.takeIf { it.isNotBlank() }?.let {
                Spacer(Modifier.height(HavnSpacing.sm))
                Text(
                    text = it,
                    color = tokens.primaryAccent,
                    style = MaterialTheme.typography.headlineSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            Spacer(Modifier.height(HavnSpacing.md))
            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                PropertyMetric(value = visitorCount.toString(), label = "leads")
                PropertyMetric(value = hotLeadCount.toString(), label = "hot leads")
            }
            Spacer(Modifier.height(HavnSpacing.md))
            Text(
                text = "Next Open House · $nextOpenHouseTime",
                color = tokens.secondaryAccent,
                style = MaterialTheme.typography.labelLarge
            )
        }
    }
}

@Composable
fun HavnLeadCard(
    name: String,
    contact: String,
    sourceProperty: String,
    temperature: HavnStatus,
    lastActivityTime: String,
    avatarDataUrl: String? = null,
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null
) {
    val tokens = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier, onClick = onClick) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnLeadAvatar(name = name, avatarDataUrl = avatarDataUrl)
            Column(Modifier.weight(1f)) {
                Text(name, color = tokens.primaryText, style = MaterialTheme.typography.titleLarge)
                Spacer(Modifier.height(HavnSpacing.xs))
                Text(contact, color = tokens.secondaryText, style = MaterialTheme.typography.bodyLarge)
                Spacer(Modifier.height(HavnSpacing.sm))
                Text(sourceProperty, color = tokens.secondaryAccent, style = MaterialTheme.typography.labelLarge)
                Spacer(Modifier.height(HavnSpacing.xs))
                Text(lastActivityTime, color = tokens.mutedText, style = MaterialTheme.typography.labelLarge)
            }
            HavnStatusBadge(temperature)
        }
    }
}

@Composable
private fun HavnLeadAvatar(name: String, avatarDataUrl: String?) {
    val tokens = LocalHavnThemeTokens.current
    val initials = name
        .split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "H" }
    Box(
        modifier = Modifier
            .size(48.dp)
            .clip(MaterialTheme.shapes.large)
            .background(Brush.linearGradient(listOf(tokens.secondaryAccent.copy(alpha = 0.72f), tokens.surface)))
            .border(1.dp, tokens.primaryAccent.copy(alpha = 0.45f), MaterialTheme.shapes.large),
        contentAlignment = Alignment.Center
    ) {
        val rendered = HavnDatabaseImage(
            dataUrl = avatarDataUrl,
            previewDataUrl = havnContactAvatarPreviewSource(avatarDataUrl, 72),
            contentDescription = name,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )
        if (!rendered) {
            Text(
                text = initials,
                color = tokens.primaryText,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
fun HavnStatCard(
    value: String,
    label: String,
    modifier: Modifier = Modifier,
    trend: String? = null,
    icon: HavnIconKey? = null
) {
    val tokens = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column {
            Row(verticalAlignment = Alignment.CenterVertically) {
                if (icon != null) {
                    HavnIcon(
                        icon = icon,
                        contentDescription = null,
                        modifier = Modifier.size(18.dp),
                        tint = tokens.primaryAccent
                    )
                    Spacer(Modifier.width(HavnSpacing.sm))
                }
                Text(label, color = tokens.secondaryText, style = MaterialTheme.typography.labelLarge)
            }
            Spacer(Modifier.height(HavnSpacing.sm))
            Text(value, color = tokens.primaryText, style = MaterialTheme.typography.headlineMedium)
            if (trend != null) {
                Spacer(Modifier.height(HavnSpacing.xs))
                Text(trend, color = tokens.primaryAccent, style = MaterialTheme.typography.labelLarge)
            }
        }
    }
}

@Composable
fun HavnStatusBadge(status: HavnStatus, modifier: Modifier = Modifier) {
    val tokens = LocalHavnThemeTokens.current
    val color = when (status) {
        HavnStatus.Active -> tokens.success
        HavnStatus.Planned -> tokens.cold
        HavnStatus.Archived -> tokens.mutedText
        HavnStatus.Hot -> Color(0xFFE85D4A)
        HavnStatus.Warm -> Color(0xFFD99A32)
        HavnStatus.Cold -> Color(0xFF35C9D0)
    }
    val backgroundAlpha = when (status) {
        HavnStatus.Active -> 1f
        HavnStatus.Planned -> 1f
        HavnStatus.Archived -> 0.92f
        HavnStatus.Hot,
        HavnStatus.Warm,
        HavnStatus.Cold -> 0.9f
    }
    val background = color.copy(alpha = backgroundAlpha)
    val contentColor = background.readableTextColor()
    val strings = LocalHavnStrings.current
    val label = when (status) {
        HavnStatus.Active -> strings.commonActive
        HavnStatus.Planned -> strings.commonPlanned
        HavnStatus.Archived -> strings.commonArchived
        HavnStatus.Hot -> strings.commonHot
        HavnStatus.Warm -> strings.commonWarm
        HavnStatus.Cold -> strings.commonCold
    }.toHavnBadgeLabel()
    val labelStyle = MaterialTheme.typography.labelLarge.copy(
        fontSize = when {
            label.length > 10 -> 9.sp
            label.length > 8 -> 10.sp
            else -> MaterialTheme.typography.labelLarge.fontSize
        }
    )
    Surface(
        modifier = modifier.widthIn(max = 82.dp),
        color = background,
        contentColor = contentColor,
        shape = MaterialTheme.shapes.small,
        border = BorderStroke(1.dp, color.copy(alpha = 0.9f))
    ) {
        Text(
            text = label,
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
            color = contentColor,
            style = labelStyle,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

private fun String.toHavnBadgeLabel(): String {
    val normalized = lowercase()
    return normalized.replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
}

private fun Color.readableTextColor(): Color {
    val luminance = (0.299f * red) + (0.587f * green) + (0.114f * blue)
    return if (luminance > 0.58f) Color.Black else Color.White
}

@Composable
fun HavnTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    placeholder: String? = null,
    leadingIcon: HavnIconKey? = null,
    isError: Boolean = false,
    errorText: String? = null,
    enabled: Boolean = true,
    singleLine: Boolean = true,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default
) {
    val tokens = LocalHavnThemeTokens.current
    val capitalizationKeyboardOptions = if (
        keyboardOptions.capitalization == KeyboardCapitalization.None &&
        keyboardOptions.keyboardType in listOf(KeyboardType.Unspecified, KeyboardType.Text, KeyboardType.Ascii)
    ) {
        keyboardOptions.copy(capitalization = KeyboardCapitalization.Sentences)
    } else {
        keyboardOptions
    }
    val effectiveKeyboardOptions = if (
        havnShouldAvoidFloatingNumericKeyboard() &&
        capitalizationKeyboardOptions.keyboardType in listOf(
            KeyboardType.Number,
            KeyboardType.Decimal,
            KeyboardType.Phone,
            KeyboardType.NumberPassword
        )
    ) {
        capitalizationKeyboardOptions.copy(keyboardType = KeyboardType.Text)
    } else {
        capitalizationKeyboardOptions
    }
    val autoCapitalizeFirstLetter = effectiveKeyboardOptions.keyboardType in listOf(
        KeyboardType.Unspecified,
        KeyboardType.Text,
        KeyboardType.Ascii
    )
    Column(modifier) {
        OutlinedTextField(
            value = value,
            onValueChange = { nextValue ->
                onValueChange(if (autoCapitalizeFirstLetter) nextValue.capitalizedFirstLetter() else nextValue)
            },
            modifier = Modifier.fillMaxWidth(),
            enabled = enabled,
            label = { Text(label) },
            placeholder = placeholder?.let { { Text(it) } },
            leadingIcon = leadingIcon?.let {
                {
                    HavnIcon(
                        icon = it,
                        contentDescription = null,
                        modifier = Modifier.size(20.dp)
                    )
                }
            },
            isError = isError,
            singleLine = singleLine,
            visualTransformation = visualTransformation,
            keyboardOptions = effectiveKeyboardOptions,
            shape = MaterialTheme.shapes.medium,
            colors = OutlinedTextFieldDefaults.colors(
                focusedTextColor = tokens.primaryText,
                unfocusedTextColor = tokens.primaryText,
                focusedContainerColor = tokens.card,
                unfocusedContainerColor = tokens.surface,
                disabledContainerColor = tokens.surface,
                errorContainerColor = tokens.surface,
                focusedBorderColor = tokens.primaryAccent,
                unfocusedBorderColor = tokens.border,
                errorBorderColor = tokens.danger,
                focusedLabelColor = tokens.secondaryAccent,
                unfocusedLabelColor = tokens.secondaryText,
                errorLabelColor = tokens.danger,
                cursorColor = tokens.primaryAccent
            )
        )
        if (isError && errorText != null) {
            Spacer(Modifier.height(HavnSpacing.xs))
            Text(errorText, color = tokens.danger, style = MaterialTheme.typography.labelLarge)
        }
    }
}

private fun String.capitalizedFirstLetter(): String {
    val index = indexOfFirst { it.isLetter() }
    if (index < 0) return this
    val char = this[index]
    if (!char.isLowerCase()) return this
    return replaceRange(index, index + 1, char.titlecase())
}

@Composable
fun HavnSearchField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier
) {
    val tokens = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier
            .fillMaxWidth()
            .height(46.dp),
        shape = MaterialTheme.shapes.medium,
        color = tokens.card.copy(alpha = 0.84f),
        border = BorderStroke(1.dp, tokens.border.copy(alpha = 0.82f))
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            HavnIcon(
                icon = HavnIconKey.Search,
                contentDescription = null,
                tint = tokens.secondaryText,
                modifier = Modifier.size(17.dp)
            )
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                singleLine = true,
                textStyle = MaterialTheme.typography.bodyMedium.copy(color = tokens.primaryText),
                cursorBrush = SolidColor(tokens.primaryAccent),
                modifier = Modifier.weight(1f),
                decorationBox = { innerTextField ->
                    if (value.isBlank()) {
                        Text(
                            text = placeholder,
                            color = tokens.secondaryText,
                            style = MaterialTheme.typography.bodyMedium,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    innerTextField()
                }
            )
        }
    }
}

@Composable
fun HavnListFilterChip(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val tokens = LocalHavnThemeTokens.current
    if (havnUsesLiquidButtons) {
        val shape = RoundedCornerShape(22.dp)
        val glassFill = Brush.verticalGradient(
            if (selected) {
                listOf(
                    tokens.primaryText.copy(alpha = 0.1f),
                    tokens.secondaryAccent.copy(alpha = 0.18f),
                    tokens.card.copy(alpha = 0.42f)
                )
            } else {
                listOf(
                    tokens.primaryText.copy(alpha = 0.05f),
                    tokens.card.copy(alpha = 0.34f),
                    tokens.surface.copy(alpha = 0.24f)
                )
            }
        )
        Box(
            modifier = modifier
                .height(HavnListDefaults.filterChipHeight)
                .alpha(if (enabled) 1f else 0.46f)
                .clip(shape)
                .background(glassFill)
                .border(
                    BorderStroke(
                        1.dp,
                        if (selected) tokens.secondaryAccent.copy(alpha = 0.38f) else tokens.primaryText.copy(alpha = 0.14f)
                    ),
                    shape
                )
                .then(if (enabled) Modifier.clickable(onClick = onClick) else Modifier)
                .fillMaxSize()
                .padding(horizontal = 4.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = label,
                color = if (selected) tokens.secondaryAccent else tokens.primaryText.copy(alpha = 0.92f),
                style = MaterialTheme.typography.labelMedium,
                fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
        return
    }

    Surface(
        shape = RoundedCornerShape(9.dp),
        color = if (selected) tokens.primaryAccent else tokens.card,
        border = BorderStroke(1.dp, if (selected) tokens.primaryAccent else tokens.border),
        modifier = modifier
            .height(HavnListDefaults.filterChipHeight)
            .then(if (enabled) Modifier.clickable(onClick = onClick) else Modifier)
            .alpha(if (enabled) 1f else 0.46f)
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 4.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = label,
                color = if (selected) tokens.background else tokens.primaryText,
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.Medium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
fun HavnListRowSeparator(
    modifier: Modifier = Modifier
) {
    HorizontalDivider(
        modifier = modifier
            .fillMaxWidth()
            .padding(start = HavnListDefaults.rowSeparatorInset),
        thickness = 0.6.dp,
        color = LocalHavnThemeTokens.current.border.copy(alpha = 0.55f)
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AgentDashboardShell(
    title: String,
    bottomBar: @Composable () -> Unit,
    modifier: Modifier = Modifier,
    actions: @Composable () -> Unit = {},
    content: @Composable (PaddingValues) -> Unit
) {
    val tokens = LocalHavnThemeTokens.current
    Scaffold(
        modifier = modifier.fillMaxSize(),
        containerColor = tokens.background,
        topBar = {
            CenterAlignedTopAppBar(
                title = { Text(title, color = tokens.primaryText, style = MaterialTheme.typography.titleLarge) },
                actions = { actions() },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = tokens.surface,
                    titleContentColor = tokens.primaryText,
                    actionIconContentColor = tokens.primaryAccent
                )
            )
        },
        bottomBar = bottomBar,
        content = content
    )
}

@Composable
fun VisitorKioskShell(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit
) {
    val tokens = LocalHavnThemeTokens.current
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    listOf(tokens.gradientStart, tokens.background, tokens.gradientEnd)
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        content()
    }
}

@Composable
fun EmptyState(
    title: String,
    body: String,
    modifier: Modifier = Modifier,
    actionText: String? = null,
    onAction: (() -> Unit)? = null
) {
    val tokens = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = HavnSpacing.lg),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(CircleShape)
                    .background(tokens.primaryAccent.copy(alpha = 0.15f)),
                contentAlignment = Alignment.Center
            ) {
                Box(
                    modifier = Modifier
                        .size(14.dp)
                        .clip(CircleShape)
                        .background(tokens.primaryAccent)
                )
            }
            Spacer(Modifier.height(HavnSpacing.md))
            Text(
                text = title,
                color = tokens.primaryText,
                style = MaterialTheme.typography.titleLarge,
                textAlign = androidx.compose.ui.text.style.TextAlign.Center
            )
            Spacer(Modifier.height(HavnSpacing.sm))
            Text(
                text = body,
                color = tokens.secondaryText,
                style = MaterialTheme.typography.bodyLarge,
                textAlign = androidx.compose.ui.text.style.TextAlign.Center
            )
            if (actionText != null && onAction != null) {
                Spacer(Modifier.height(HavnSpacing.lg))
                HavnButton(
                    actionText,
                    onClick = onAction,
                    modifier = Modifier
                        .widthIn(max = 420.dp)
                        .fillMaxWidth()
                )
            }
        }
    }
}

@Composable
fun LoadingState(
    label: String,
    modifier: Modifier = Modifier
) {
    val tokens = LocalHavnThemeTokens.current
    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(vertical = HavnSpacing.xl),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(22.dp),
            color = tokens.primaryAccent,
            strokeWidth = 2.dp
        )
        Spacer(Modifier.width(HavnSpacing.md))
        Text(label, color = tokens.secondaryText, style = MaterialTheme.typography.bodyLarge)
    }
}

@Composable
fun HavnSkeletonBlock(
    modifier: Modifier = Modifier,
    shape: androidx.compose.ui.graphics.Shape = MaterialTheme.shapes.small
) {
    Box(
        modifier = modifier
            .clip(shape)
            .background(havnSkeletonBrush(), shape = shape)
    )
}

@Composable
fun HavnSkeletonListFooter(
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(vertical = 10.dp),
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm, Alignment.CenterHorizontally),
        verticalAlignment = Alignment.CenterVertically
    ) {
        HavnSkeletonBlock(Modifier.width(74.dp).height(9.dp), shape = RoundedCornerShape(9.dp))
        HavnSkeletonBlock(Modifier.width(132.dp).height(9.dp), shape = RoundedCornerShape(9.dp))
        HavnSkeletonBlock(Modifier.width(58.dp).height(9.dp), shape = RoundedCornerShape(9.dp))
    }
}

@Composable
private fun havnSkeletonBrush(): Brush {
    val tokens = LocalHavnThemeTokens.current
    val transition = rememberInfiniteTransition(label = "havn-skeleton-shimmer")
    val shimmerOffset by transition.animateFloat(
        initialValue = -360f,
        targetValue = 960f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 1250, easing = LinearEasing),
            repeatMode = RepeatMode.Restart
        ),
        label = "havn-skeleton-offset"
    )
    val base = tokens.surfaceElevated.copy(alpha = 0.86f)
    val glow = tokens.primaryText.copy(alpha = 0.10f)
    val edge = tokens.surface.copy(alpha = 0.72f)
    return Brush.linearGradient(
        colors = listOf(base, edge, glow, edge, base),
        start = Offset(shimmerOffset - 280f, 0f),
        end = Offset(shimmerOffset, 0f)
    )
}

@Composable
fun LuxuryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    loading: Boolean = false
) {
    HavnButton(
        text = text,
        onClick = onClick,
        modifier = modifier,
        enabled = enabled,
        loading = loading
    )
}

@Composable
fun StatCard(
    label: String,
    value: String,
    detail: String,
    modifier: Modifier = Modifier
) {
    HavnStatCard(value = value, label = label, trend = detail, modifier = modifier)
}

@Composable
fun PropertyCard(
    title: String,
    address: String,
    price: String,
    detail: String,
    modifier: Modifier = Modifier,
    status: String = "Active",
    imageHeader: (@Composable BoxScope.() -> Unit)? = null
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard(
        modifier = modifier,
        imageHeader = {
            if (imageHeader == null) {
                DefaultPropertyImage()
            } else {
                imageHeader()
            }
            HavnStatusBadge(
                status = HavnStatus.fromLabel(status),
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .padding(HavnSpacing.md)
            )
            Text(
                text = price,
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .padding(HavnSpacing.md),
                color = theme.primaryText,
                style = MaterialTheme.typography.headlineMedium
            )
        }
    ) {
        Column {
            Text(title, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            Spacer(Modifier.height(HavnSpacing.xs))
            Text(address, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
            Spacer(Modifier.height(HavnSpacing.sm))
            Text(detail, color = theme.secondaryAccent, style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
fun LeadStatusBadge(status: LeadStatus, modifier: Modifier = Modifier) {
    HavnStatusBadge(status = status.toHavnStatus(), modifier = modifier)
}

enum class HavnStatus(val label: String, val color: Color) {
    Active("Active", HavnSuccess),
    Planned("Planned", HavnCold),
    Archived("Archived", HavnTextMuted),
    Hot("Hot", HavnDanger),
    Warm("Warm", HavnGold),
    Cold("Cold", HavnCold);

    companion object {
        fun fromLabel(label: String): HavnStatus =
            entries.firstOrNull { it.label.equals(label, ignoreCase = true) } ?: Active
    }
}

enum class LeadStatus(val label: String) {
    Hot("Hot"),
    Warm("Warm"),
    Cold("Cold")
}

private fun LeadStatus.toHavnStatus(): HavnStatus = when (this) {
    LeadStatus.Hot -> HavnStatus.Hot
    LeadStatus.Warm -> HavnStatus.Warm
    LeadStatus.Cold -> HavnStatus.Cold
}

@Composable
private fun PropertyMetric(value: String, label: String) {
    val theme = LocalHavnThemeTokens.current
    Column {
        Text(value, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
        Text(label, color = theme.mutedText, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun BoxScope.DefaultPropertyImage() {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(PropertyPhotographyBrush)
    )
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    colors = listOf(Color.Transparent, theme.background.copy(alpha = 0.76f))
                )
            )
    )
}

private val PropertyPhotographyBrush = Brush.linearGradient(
    colors = listOf(
        Color(0xFF2A3440),
        Color(0xFF6E5A32),
        Color(0xFF0B1118),
        Color(0xFF213343)
    )
)
